import React from "react"
import ReactMarkdown from "react-markdown"
import ClassificationBadge from "./ClassificationBadge"
import SourceCard from "./SourceCard"
import SuggestedQuestions from "./SuggestedQuestions"

// Inject keyframe animations once at module level
if (typeof document !== "undefined" && !document.getElementById("streaming-msg-keyframes")) {
  const style = document.createElement("style")
  style.id = "streaming-msg-keyframes"
  style.textContent = `
    @keyframes pulse { 0%, 100% { opacity: 0.3; transform: scale(0.8); } 50% { opacity: 1; transform: scale(1.2); } }
    @keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
    .streaming-message-content p:last-child { margin-bottom: 0 !important; }
  `
  document.head.appendChild(style)
}

const ThinkingIndicator = ({ step }) => {
  const labels = {
    classifying: "Classifying your question...",
    querying: "Searching data sources...",
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "4px 0" }}>
      <div
        style={{
          width: "8px",
          height: "8px",
          borderRadius: "50%",
          backgroundColor: "#407775",
          animation: "pulse 1.2s infinite",
        }}
      />
      <span style={{ fontSize: "13px", color: "#707070", fontStyle: "italic" }}>
        {labels[step] || "Thinking..."}
      </span>
    </div>
  )
}

const StreamingMessage = ({ message, onFollowUp }) => {
  const isUser = message.role === "user"

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: isUser ? "flex-end" : "flex-start",
        marginBottom: "16px",
      }}
    >
      {/* User message */}
      {isUser && (
        <div
          style={{
            backgroundColor: "#407775",
            color: "#fff",
            padding: "10px 16px",
            borderRadius: "16px 16px 4px 16px",
            maxWidth: "85%",
            fontSize: "13px",
            lineHeight: "1.5",
          }}
        >
          {message.content}
        </div>
      )}

      {/* Assistant message */}
      {!isUser && (
        <div style={{ maxWidth: "95%", width: "100%" }}>
          {/* Classification badge */}
          {message.classification && (
            <ClassificationBadge
              category={message.classification.category}
              confidence={message.classification.confidence}
            />
          )}

          {/* Thinking indicator */}
          {message.isStreaming && !message.content && (
            <ThinkingIndicator step={message.classification ? "querying" : "classifying"} />
          )}

          {/* Message content */}
          {message.content && (
            <div
              style={{
                backgroundColor: "#F8F9FA",
                padding: "12px 16px",
                borderRadius: "4px 16px 16px 16px",
                fontSize: "13px",
                lineHeight: "1.6",
                color: "#494949",
              }}
              className="streaming-message-content"
            >
              <ReactMarkdown
                components={{
                  p: ({ children }) => <p style={{ margin: "0 0 8px 0" }}>{children}</p>,
                  strong: ({ children }) => (
                    <strong style={{ color: "#407775" }}>{children}</strong>
                  ),
                  ul: ({ children }) => (
                    <ul style={{ margin: "4px 0", paddingLeft: "20px" }}>{children}</ul>
                  ),
                  li: ({ children }) => <li style={{ marginBottom: "2px" }}>{children}</li>,
                  table: ({ children }) => (
                    <div style={{ overflowX: "auto", margin: "8px 0" }}>
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          fontSize: "12px",
                        }}
                      >
                        {children}
                      </table>
                    </div>
                  ),
                  th: ({ children }) => (
                    <th
                      style={{
                        padding: "6px 10px",
                        backgroundColor: "#407775",
                        color: "#fff",
                        textAlign: "left",
                        fontWeight: "600",
                        fontSize: "11px",
                      }}
                    >
                      {children}
                    </th>
                  ),
                  td: ({ children }) => (
                    <td
                      style={{
                        padding: "5px 10px",
                        borderBottom: "1px solid #E5E5E5",
                      }}
                    >
                      {children}
                    </td>
                  ),
                }}
              >
                {message.content}
              </ReactMarkdown>
              {message.isStreaming && (
                <span
                  style={{
                    display: "inline-block",
                    width: "6px",
                    height: "14px",
                    backgroundColor: "#407775",
                    marginLeft: "2px",
                    animation: "blink 0.8s infinite",
                    verticalAlign: "text-bottom",
                  }}
                />
              )}
            </div>
          )}

          {/* Sources */}
          {message.sources && message.sources.length > 0 && !message.isStreaming && (
            <div style={{ marginTop: "8px" }}>
              <span style={{ fontSize: "10px", color: "#707070", fontWeight: "600", letterSpacing: "0.5px" }}>
                SOURCES
              </span>
              <div style={{ marginTop: "4px" }}>
                {message.sources.map((s, i) => (
                  <SourceCard key={i} source={s} />
                ))}
              </div>
            </div>
          )}

          {/* Follow-up suggestions */}
          {!message.isStreaming && message.content && onFollowUp && (
            <SuggestedQuestions
              onSelect={onFollowUp}
              category={message.classification?.category}
              isFollowUp
            />
          )}
        </div>
      )}

    </div>
  )
}

export default StreamingMessage
